import type { DataRepository } from '@/types/repository';
import type { StructureItem } from '@/types/database';
import { isWebCompanionPlatform } from '@/utils/platform';
import { androidRepository } from '@/lib/repository/androidRepository';
import { webRepository } from '@/lib/repository/webRepository';

/**
 * Creates a complete copy of a manuscript and its associated DataRepository data
 * (planner structure, scene content, codex, goals, formatting) under a new ID.
 * Safely maps scene and codex IDs to avoid overlapping references.
 * Returns the new manuscript ID.
 */
export async function createConflictCopy(
  originalId: string,
  repo?: DataRepository
): Promise<string> {
  const targetRepo = repo || (isWebCompanionPlatform() ? webRepository : androidRepository);
  const newManuscriptId = crypto.randomUUID();
  
  // Fetch original data
  const [manuscript, planner, codex, goals, format, scenes] = await Promise.all([
    targetRepo.getManuscript(originalId),
    targetRepo.getPlannerStructure(originalId),
    targetRepo.getCodexEntriesForManuscript(originalId),
    targetRepo.getGoals(originalId),
    targetRepo.getFormatSettings(originalId),
    targetRepo.getAllSceneContentForManuscript(originalId)
  ]);

  if (!manuscript) return originalId;

  // 1. Clone manuscript metadata
  const clonedManuscript = {
    ...manuscript,
    id: newManuscriptId,
    title: `[Conflict Copy] ${manuscript.title}`,
    lastModified: new Date()
  };
  await targetRepo.putManuscript(clonedManuscript);

  // ID mapping tables to maintain outline integrity
  const itemIdMap = new Map<string, string>();
  const codexIdMap = new Map<string, string>();

  // 2. Clone codex entries and map their IDs
  if (codex && codex.length > 0) {
    await Promise.all(codex.map(async (entry) => {
      const newEntryId = crypto.randomUUID();
      codexIdMap.set(entry.id, newEntryId);
      
      await targetRepo.putCodexEntry({
        ...entry,
        id: newEntryId,
        manuscriptId: newManuscriptId
      });
    }));
  }

  // Recursive mapper for planner structure tree
  const mapStructureItems = (items: StructureItem[]): StructureItem[] => {
    return items.map(item => {
      const newItemId = crypto.randomUUID();
      itemIdMap.set(item.id, newItemId);

      // Remap pov character references
      let newMetadata = item.metadata;
      if (item.metadata?.povCharacterIds) {
        newMetadata = {
          ...item.metadata,
          povCharacterIds: item.metadata.povCharacterIds.map(id => codexIdMap.get(id) || id)
        };
      }

      return {
        ...item,
        id: newItemId,
        metadata: newMetadata,
        children: item.children ? mapStructureItems(item.children) : undefined
      };
    });
  };

  // 3. Clone planner structure with mapped IDs
  if (planner && planner.structure) {
    const clonedStructure = mapStructureItems(planner.structure);
    await targetRepo.putPlannerStructure({
      id: newManuscriptId,
      structure: clonedStructure
    });
  }

  // 4. Clone scene content using mapped IDs
  if (scenes && scenes.length > 0) {
    await Promise.all(scenes.map(async (scene) => {
      const newSceneId = itemIdMap.get(scene.id) || crypto.randomUUID();
      await targetRepo.putSceneContent({
        ...scene,
        id: newSceneId,
        manuscriptId: newManuscriptId
      });
    }));
  }

  // 5. Clone goals
  if (goals) {
    await targetRepo.putGoals({
      ...goals,
      id: newManuscriptId
    });
  }

  // 6. Clone format settings
  if (format) {
    await targetRepo.putFormatSettings({
      ...format,
      id: newManuscriptId
    });
  }

  // 7. Update parent Series if member
  if (manuscript.seriesId) {
    const series = await targetRepo.getSeries(manuscript.seriesId);
    if (series && !series.manuscripts.includes(newManuscriptId)) {
      const idx = series.manuscripts.indexOf(originalId);
      const updatedList = [...series.manuscripts];
      if (idx !== -1) {
        updatedList.splice(idx + 1, 0, newManuscriptId);
      } else {
        updatedList.push(newManuscriptId);
      }
      await targetRepo.putSeries({ ...series, manuscripts: updatedList });
    }
  }

  // 8. Update parent Collection if member
  if (manuscript.collectionId) {
    const collection = await targetRepo.getCollection(manuscript.collectionId);
    if (collection && !collection.manuscripts.includes(newManuscriptId)) {
      await targetRepo.putCollection({ ...collection, manuscripts: [...collection.manuscripts, newManuscriptId] });
    }
  }

  return newManuscriptId;
}
